

import React, { useContext, useEffect, useState } from 'react';
import { landingPageContext } from '../LandingEdits';

interface LiveClass {
  id: string;
  tabId: string;
  tag: string;
  subtitle: string;
  title: string;
  status: 'LIVE NOW' | 'SESSION ENDED' | 'COMING SOON';
  time: string;
  actionText: string;
  instructor: string;
  type: string;
  image: string; // Base64 image
}

const TABS = [
  { id: 'general', title: 'General Studies' },
  { id: 'optional', title: 'Optional' },
];

const AdminLiveClassEditor: React.FC = () => {
  const context = useContext(landingPageContext);
  if (!context) throw new Error('Must be used within LandingEdits');

  const { landingDetails, setLandingDetails } = context;

  const [selectedType, setSelectedType] = useState<'general' | 'optional'>('general');
  const [liveClasses, setLiveClasses] = useState<LiveClass[]>([]);

  useEffect(() => {
    setLiveClasses(landingDetails.live_class || []);
  }, [landingDetails.live_class]);

  const updateContext = (updated: LiveClass[]) => {
    setLiveClasses(updated);
    setLandingDetails(prev => ({
      ...prev,
      live_class: updated,
    }));
  };

  const handleInputChange = (id: string, field: keyof LiveClass, value: string) => {
    const updated = liveClasses.map(c =>
      c.id === id ? { ...c, [field]: value } : c
    );
    updateContext(updated);
  };

  const handleAddClass = () => {
    const newClass: LiveClass = {
      id: Date.now().toString(),
      tabId: Date.now().toString(),
      type: selectedType,
      tag: 'FREE',
      subtitle: '',
      title: '',
      status: 'COMING SOON',
      time: '',
      actionText: 'Remind Me',
      instructor: '',
      image: '',
    };
    updateContext([...liveClasses, newClass]);
  };

  const handleDeleteClass = (id: string) => {
    const updated = liveClasses.filter(c => c.id !== id);
    updateContext(updated);
  };

  const toBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = error => reject(error);
    });
  };

  const handleImageUpload = async (id: string, file: File) => {
    const base64 = await toBase64(file);
    const updated = liveClasses.map(cls =>
      cls.id === id ? { ...cls, image: base64 } : cls
    );
    updateContext(updated);
  };

  const filteredClasses = liveClasses.filter(c => c.type === selectedType);

  return (
    <div className="p-6 space-y-6">
      <h2 className="text-2xl font-bold">Live Classes Editor</h2>

      {/* Tabs */}
      <div className="flex gap-4 flex-wrap">
        {TABS.map(tab => (
          <button
            key={tab.id}
            className={`px-4 py-2 rounded ${
              selectedType === tab.id ? 'bg-teal-600 text-white' : 'bg-gray-200 text-black'
            }`}
            onClick={() => setSelectedType(tab.id as 'general' | 'optional')}
          >
            {tab.title}
          </button>
        ))}
      </div>

      {/* Add Button */}
      <button
        onClick={handleAddClass}
        className="bg-green-600 text-white px-4 py-2 rounded"
      >
        + Add New Class
      </button>

      {/* Class Editor */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredClasses.map(cls => (
          <div key={cls.id} className="border rounded-xl p-4 space-y-3 shadow">
            <label className="block text-sm font-medium">Tag</label>
            <input
              type="text"
              value={cls.tag}
              onChange={e => handleInputChange(cls.id, 'tag', e.target.value)}
              className="w-full border p-2 rounded"
            />

            <label className="block text-sm font-medium">Subtitle</label>
            <input
              type="text"
              value={cls.subtitle}
              onChange={e => handleInputChange(cls.id, 'subtitle', e.target.value)}
              className="w-full border p-2 rounded"
            />

            <label className="block text-sm font-medium">Title</label>
            <input
              type="text"
              value={cls.title}
              onChange={e => handleInputChange(cls.id, 'title', e.target.value)}
              className="w-full border p-2 rounded"
            />

            <label className="block text-sm font-medium">Status</label>
            <select
              value={cls.status}
              onChange={e => handleInputChange(cls.id, 'status', e.target.value)}
              className="w-full border p-2 rounded"
            >
              <option value="LIVE NOW">LIVE NOW</option>
              <option value="SESSION ENDED">SESSION ENDED</option>
              <option value="COMING SOON">COMING SOON</option>
            </select>

            <label className="block text-sm font-medium">Time</label>
            <input
              type="text"
              value={cls.time}
              onChange={e => handleInputChange(cls.id, 'time', e.target.value)}
              className="w-full border p-2 rounded"
            />

            <label className="block text-sm font-medium">Button Text</label>
            <input
              type="text"
              value={cls.actionText}
              onChange={e => handleInputChange(cls.id, 'actionText', e.target.value)}
              className="w-full border p-2 rounded"
            />

            <label className="block text-sm font-medium">Instructor</label>
            <input
              type="text"
              value={cls.instructor}
              onChange={e => handleInputChange(cls.id, 'instructor', e.target.value)}
              className="w-full border p-2 rounded"
            />

            <label className="block text-sm font-medium">Image</label>
            <input
              type="file"
              accept="image/*"
              onChange={e => {
                const file = e.target.files?.[0];
                if (file) handleImageUpload(cls.id, file);
              }}
              className="w-full border p-2 rounded"
            />

            {cls.image && (
              <img
                src={cls.image}
                alt="Preview"
                className="w-full h-40 object-cover mt-2 rounded"
              />
            )}

            <button
              onClick={() => handleDeleteClass(cls.id)}
              className="bg-red-500 text-white px-4 py-2 rounded w-full mt-2"
            >
              Delete
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};

export default AdminLiveClassEditor;
